import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Phone, Mail, Building, User, Trash2, Edit, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contacts = () => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    company: '',
    email: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async (search = '') => {
    try {
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/contacts`, { params });
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    fetchContacts(value);
  };

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name || '',
        role: contact.role || '',
        company: contact.company || '',
        email: contact.email || '',
        phone: contact.phone || '',
        notes: contact.notes || ''
      });
    } else {
      setEditingContact(null);
      setFormData({
        name: '',
        role: '',
        company: '',
        email: '',
        phone: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Bitte Name eingeben');
      return;
    }
    
    // Clean form data - send null for empty optional fields
    const cleanedData = {
      name: formData.name,
      role: formData.role || null,
      company: formData.company || null,
      email: formData.email || null,
      phone: formData.phone || null,
      notes: formData.notes || null
    };
    
    setSaving(true);
    try {
      if (editingContact) {
        const response = await axios.put(`${API}/contacts/${editingContact.id}`, cleanedData);
        setContacts(contacts.map(c => c.id === editingContact.id ? response.data : c));
        toast.success('Kontakt aktualisiert');
      } else {
        const response = await axios.post(`${API}/contacts`, cleanedData);
        setContacts([response.data, ...contacts]);
        toast.success(t('contactCreated'));
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      setContacts(contacts.filter(c => c.id !== contactId));
      toast.success('Kontakt gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-[Manrope]">{t('contacts')}</h2>
          <p className="text-slate-500 mt-1 text-sm">{contacts.length} Kontakte</p>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t('search') + '...'}
              className="pl-10 w-full sm:w-64 h-9"
              data-testid="contact-search"
            />
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#D92632] hover:bg-[#B91C26] text-white h-9 text-sm"
                onClick={() => handleOpenDialog()}
                data-testid="new-contact-btn"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('newContact')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">
                  {editingContact ? t('edit') : t('newContact')}
                </DialogTitle>
                <DialogDescription>
                  {editingContact ? 'Kontaktdetails bearbeiten' : 'Neuen Kontakt hinzufügen'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>{t('contactName')} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Max Mustermann"
                    className="mt-1"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('role')}</Label>
                    <Input
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      placeholder="z.B. Geschäftsführer"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t('company')}</Label>
                    <Input
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Firma GmbH"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('email')}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="name@email.de"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t('phone')}</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+49 123 456789"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>{t('notes')}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optionale Notizen..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button 
                    onClick={handleSave}
                    className="bg-[#D92632] hover:bg-[#B91C26]"
                    disabled={saving}
                    data-testid="save-contact-btn"
                  >
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t('save')}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Contacts Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : contacts.length === 0 ? (
        <Card className="bg-white border border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">
            {searchTerm ? 'Keine Kontakte gefunden' : t('noData')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {contacts.map((contact) => (
            <Card 
              key={contact.id} 
              className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`contact-${contact.id}`}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#D92632]/10 flex items-center justify-center text-[#D92632] font-semibold">
                      {contact.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{contact.name}</h3>
                      {contact.role && (
                        <p className="text-sm text-slate-500">{contact.role}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-slate-600"
                      onClick={() => handleOpenDialog(contact)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-red-500"
                      onClick={() => handleDelete(contact.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4 space-y-2">
                  {contact.company && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Building className="h-4 w-4 text-slate-400" />
                      {contact.company}
                    </div>
                  )}
                  {contact.email && (
                    <a 
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#D92632]"
                    >
                      <Mail className="h-4 w-4 text-slate-400" />
                      {contact.email}
                    </a>
                  )}
                  {contact.phone && (
                    <a 
                      href={`tel:${contact.phone}`}
                      className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#D92632]"
                    >
                      <Phone className="h-4 w-4 text-slate-400" />
                      {contact.phone}
                    </a>
                  )}
                </div>
                
                {contact.notes && (
                  <p className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-500 line-clamp-2">
                    {contact.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Contacts;
